import { createContext, useContext, type ReactNode } from 'react'

// ============================================================================
// THE CAPTURE SURFACES, reachable from anywhere inside the mobile shell.
//
// Both of them are OVERLAYS OVER A ROUTE THAT NEVER CHANGES (README §1c and
// §Interactions → Capture), so they are mounted once, by MobileApp, above the
// <Routes>. Two things need to open them: the `+` in the tab bar, which is right
// there, and the 56px record circle in the Today composer, which is four
// components deep.
//
// A CONTEXT RATHER THAN PROPS, because the alternative is threading three
// callbacks through every screen that might one day grow a capture affordance —
// and prop-drilling a callback is how the second caller ends up wiring its own
// copy of the recorder instead.
// ============================================================================

export interface CaptureSurfaces {
  /** Open the capture sheet (1c) over whatever route is showing. */
  openSheet: () => void
  /** Open the recording surface (1d) and start the microphone. */
  startRecording: () => void
  /** The finger came up. Stops, uploads, and closes. */
  stopRecording: () => void
  /**
   * Open the capture sheet as a Reminder with `text` in the field — the Day mail sheet's
   * "Remind me" (board 23 → 10; Quick add itself is Phase 6). A half-typed draft is kept: the
   * text goes on a new line under it, never over it.
   */
  openReminder: (text: string) => void
}

/**
 * The default is three no-ops rather than a throw. A screen rendered outside the
 * mobile shell — a unit test, a Storybook-style harness — should render, not
 * explode; the buttons simply do nothing, which is visibly true.
 */
const CaptureContext = createContext<CaptureSurfaces>({
  openSheet: () => {},
  startRecording: () => {},
  stopRecording: () => {},
  openReminder: () => {},
})

export function CaptureProvider({
  value,
  children,
}: {
  value: CaptureSurfaces
  children: ReactNode
}) {
  return <CaptureContext.Provider value={value}>{children}</CaptureContext.Provider>
}

export function useCapture(): CaptureSurfaces {
  return useContext(CaptureContext)
}
